import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Wrench,
  ArrowLeft,
  MapPin,
  Calendar,
  Clock,
  FileText,
  Activity,
  PlayCircle,
  StopCircle,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import { PageHeader, StatusBadge } from '@/components/common';
import {
  equipmentApi,
  EquipmentDetail as EquipmentDetailType,
  DowntimeEvent,
  RecordDowntimeDto,
  DowntimeReason,
} from '@/api/services/equipment.api';
import { getErrorMessage } from '@/api/client';

const downtimeReasons: DowntimeReason[] = [
  'SCHEDULED_MAINTENANCE',
  'UNSCHEDULED_MAINTENANCE',
  'BREAKDOWN',
  'INSPECTION',
  'CALIBRATION',
  'OPERATOR_ERROR',
  'ENVIRONMENTAL',
  'OTHER',
];

export default function EquipmentDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [equipment, setEquipment] = useState<EquipmentDetailType | null>(null);
  const [loading, setLoading] = useState(true);

  // Downtime modal
  const [downtimeOpen, setDowntimeOpen] = useState(false);
  const [downtimeData, setDowntimeData] = useState<RecordDowntimeDto>({
    startTime: new Date().toISOString().slice(0, 16),
    reason: 'SCHEDULED_MAINTENANCE',
    description: '',
  });
  const [savingDowntime, setSavingDowntime] = useState(false);

  // Active downtime to end
  const [activeDowntime, setActiveDowntime] = useState<DowntimeEvent | null>(null);

  const fetchEquipment = async () => {
    if (!id) return;

    try {
      setLoading(true);
      const res = await equipmentApi.getById(id);
      setEquipment(res.data);

      // Find active downtime
      const active = res.data.downtimeEvents?.find((e) => !e.endTime);
      setActiveDowntime(active || null);
    } catch (error) {
      toast.error(getErrorMessage(error));
      navigate('/equipment');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEquipment();
  }, [id]);

  const handleRecordDowntime = async () => {
    if (!equipment) return;

    try {
      setSavingDowntime(true);
      await equipmentApi.recordDowntime(equipment.id, downtimeData);
      toast.success('Downtime recorded successfully');
      setDowntimeOpen(false);
      setDowntimeData({
        startTime: new Date().toISOString().slice(0, 16),
        reason: 'SCHEDULED_MAINTENANCE',
        description: '',
      });
      fetchEquipment();
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setSavingDowntime(false);
    }
  };

  const handleEndDowntime = async () => {
    if (!equipment || !activeDowntime) return;

    try {
      setSavingDowntime(true);
      await equipmentApi.endDowntime(equipment.id, activeDowntime.id, {});
      toast.success('Downtime ended successfully');
      fetchEquipment();
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setSavingDowntime(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-10 w-48" />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <Skeleton className="h-48" />
          <Skeleton className="h-48 md:col-span-2" />
        </div>
      </div>
    );
  }

  if (!equipment) {
    return null;
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title={equipment.name}
        description={`Asset #${equipment.assetNumber}`}
        actions={
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => navigate('/equipment')}>
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back
            </Button>
            {activeDowntime ? (
              <Button onClick={handleEndDowntime} disabled={savingDowntime}>
                <StopCircle className="mr-2 h-4 w-4" />
                End Downtime
              </Button>
            ) : (
              <Button onClick={() => setDowntimeOpen(true)}>
                <PlayCircle className="mr-2 h-4 w-4" />
                Record Downtime
              </Button>
            )}
          </div>
        }
      />

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Equipment Info Card */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Wrench className="h-5 w-5" />
              Equipment Details
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex gap-2">
              <StatusBadge
                status={equipment.status.toLowerCase().replace(/_/g, '-')}
                label={equipment.status.replace(/_/g, ' ')}
              />
              <StatusBadge
                status={equipment.condition.toLowerCase()}
                label={equipment.condition}
              />
            </div>

            <div className="space-y-3">
              <div className="flex items-center gap-2 text-sm">
                <MapPin className="h-4 w-4 text-muted-foreground" />
                <span>{equipment.location.name}</span>
              </div>

              <div>
                <p className="text-sm text-muted-foreground">Category</p>
                <p className="font-medium">{equipment.category.replace('_', ' ')}</p>
              </div>

              {equipment.manufacturer && (
                <div>
                  <p className="text-sm text-muted-foreground">Manufacturer</p>
                  <p className="font-medium">{equipment.manufacturer}</p>
                </div>
              )}

              {equipment.model && (
                <div>
                  <p className="text-sm text-muted-foreground">Model</p>
                  <p className="font-medium">{equipment.model}</p>
                </div>
              )}

              {equipment.serialNumber && (
                <div>
                  <p className="text-sm text-muted-foreground">Serial Number</p>
                  <p className="font-medium font-mono">{equipment.serialNumber}</p>
                </div>
              )}

              {equipment.operatingHours !== null && (
                <div>
                  <p className="text-sm text-muted-foreground">Operating Hours</p>
                  <p className="font-medium">{equipment.operatingHours} hrs</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Service & Warranty Card */}
        <Card className="md:col-span-2">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Calendar className="h-5 w-5" />
              Service & Warranty
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="p-4 bg-muted rounded-lg">
                <p className="text-sm text-muted-foreground">Purchase Date</p>
                <p className="font-medium">
                  {equipment.purchaseDate
                    ? new Date(equipment.purchaseDate).toLocaleDateString()
                    : '-'}
                </p>
              </div>
              <div className="p-4 bg-muted rounded-lg">
                <p className="text-sm text-muted-foreground">Purchase Price</p>
                <p className="font-medium">
                  {equipment.purchasePrice
                    ? `$${equipment.purchasePrice.toLocaleString()}`
                    : '-'}
                </p>
              </div>
              <div className="p-4 bg-muted rounded-lg">
                <p className="text-sm text-muted-foreground">Last Service</p>
                <p className="font-medium">
                  {equipment.lastServiceDate
                    ? new Date(equipment.lastServiceDate).toLocaleDateString()
                    : '-'}
                </p>
              </div>
              <div className="p-4 bg-muted rounded-lg">
                <p className="text-sm text-muted-foreground">Next Service</p>
                <p className="font-medium">
                  {equipment.nextServiceDate
                    ? new Date(equipment.nextServiceDate).toLocaleDateString()
                    : '-'}
                </p>
              </div>
            </div>

            {equipment.warrantyExpiry && (
              <div className="mt-4 p-4 border rounded-lg">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Warranty Expiry</p>
                    <p className="font-medium">
                      {new Date(equipment.warrantyExpiry).toLocaleDateString()}
                    </p>
                  </div>
                  {new Date(equipment.warrantyExpiry) < new Date() ? (
                    <Badge variant="destructive">Expired</Badge>
                  ) : new Date(equipment.warrantyExpiry) <
                    new Date(Date.now() + 30 * 24 * 60 * 60 * 1000) ? (
                    <Badge className="bg-yellow-100 text-yellow-800">Expiring Soon</Badge>
                  ) : (
                    <Badge variant="secondary">Active</Badge>
                  )}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Tabs for History */}
      <Tabs defaultValue="downtime" className="space-y-4">
        <TabsList>
          <TabsTrigger value="downtime">
            <Activity className="mr-2 h-4 w-4" />
            Downtime History
          </TabsTrigger>
          <TabsTrigger value="workorders">
            <FileText className="mr-2 h-4 w-4" />
            Work Orders
          </TabsTrigger>
          <TabsTrigger value="maintenance">
            <Clock className="mr-2 h-4 w-4" />
            Maintenance Schedules
          </TabsTrigger>
        </TabsList>

        <TabsContent value="downtime">
          <Card>
            <CardHeader>
              <CardTitle>Downtime Events</CardTitle>
              <CardDescription>
                Track equipment downtime and repairs
              </CardDescription>
            </CardHeader>
            <CardContent>
              {equipment.downtimeEvents.length === 0 ? (
                <p className="text-muted-foreground text-center py-8">
                  No downtime events recorded
                </p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Reason</TableHead>
                      <TableHead>Start Time</TableHead>
                      <TableHead>End Time</TableHead>
                      <TableHead>Duration</TableHead>
                      <TableHead>Reported By</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {equipment.downtimeEvents.map((event) => {
                      const duration = event.endTime
                        ? (new Date(event.endTime).getTime() -
                            new Date(event.startTime).getTime()) /
                          (1000 * 60 * 60)
                        : null;

                      return (
                        <TableRow key={event.id}>
                          <TableCell>
                            <span className="font-medium">
                              {event.reason.replace(/_/g, ' ')}
                            </span>
                            {event.description && (
                              <p className="text-xs text-muted-foreground">
                                {event.description}
                              </p>
                            )}
                          </TableCell>
                          <TableCell>
                            {new Date(event.startTime).toLocaleString()}
                          </TableCell>
                          <TableCell>
                            {event.endTime ? (
                              new Date(event.endTime).toLocaleString()
                            ) : (
                              <Badge className="bg-red-100 text-red-800">Active</Badge>
                            )}
                          </TableCell>
                          <TableCell>
                            {duration !== null
                              ? `${duration.toFixed(1)} hrs`
                              : '-'}
                          </TableCell>
                          <TableCell>{event.reportedBy.fullName}</TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="workorders">
          <Card>
            <CardHeader>
              <CardTitle>Work Orders</CardTitle>
              <CardDescription>
                Work orders associated with this equipment
              </CardDescription>
            </CardHeader>
            <CardContent>
              {equipment.workOrders.length === 0 ? (
                <p className="text-muted-foreground text-center py-8">
                  No work orders found
                </p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>WO Number</TableHead>
                      <TableHead>Title</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Scheduled</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {equipment.workOrders.map((wo) => (
                      <TableRow
                        key={wo.id}
                        className="cursor-pointer hover:bg-muted/50"
                        onClick={() => navigate(`/work-orders/${wo.id}`)}
                      >
                        <TableCell className="font-medium">
                          {wo.workOrderNumber}
                        </TableCell>
                        <TableCell>{wo.title}</TableCell>
                        <TableCell>{wo.type.replace(/_/g, ' ')}</TableCell>
                        <TableCell>
                          <Badge variant="secondary">
                            {wo.status.replace(/_/g, ' ')}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          {wo.scheduledStart
                            ? new Date(wo.scheduledStart).toLocaleDateString()
                            : '-'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="maintenance">
          <Card>
            <CardHeader>
              <CardTitle>Maintenance Schedules</CardTitle>
              <CardDescription>
                Active maintenance schedules for this equipment
              </CardDescription>
            </CardHeader>
            <CardContent>
              {equipment.maintenanceSchedules.length === 0 ? (
                <p className="text-muted-foreground text-center py-8">
                  No maintenance schedules configured
                </p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Schedule Name</TableHead>
                      <TableHead>Frequency</TableHead>
                      <TableHead>Next Due</TableHead>
                      <TableHead>Priority</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {equipment.maintenanceSchedules.map((schedule) => (
                      <TableRow key={schedule.id}>
                        <TableCell className="font-medium">{schedule.name}</TableCell>
                        <TableCell>{schedule.frequency.replace(/_/g, ' ')}</TableCell>
                        <TableCell>
                          {new Date(schedule.nextDue).toLocaleDateString()}
                        </TableCell>
                        <TableCell>
                          <Badge variant="secondary">
                            {schedule.priority}
                          </Badge>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Record Downtime Dialog */}
      <Dialog open={downtimeOpen} onOpenChange={setDowntimeOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Record Downtime</DialogTitle>
            <DialogDescription>
              Record equipment downtime for maintenance or repair
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="reason">Reason</Label>
              <Select
                value={downtimeData.reason}
                onValueChange={(v: DowntimeReason) =>
                  setDowntimeData((prev) => ({ ...prev, reason: v }))
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {downtimeReasons.map((reason) => (
                    <SelectItem key={reason} value={reason}>
                      {reason.replace(/_/g, ' ')}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="startTime">Start Time</Label>
              <Input
                id="startTime"
                type="datetime-local"
                value={downtimeData.startTime}
                onChange={(e) =>
                  setDowntimeData((prev) => ({ ...prev, startTime: e.target.value }))
                }
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                value={downtimeData.description || ''}
                onChange={(e) =>
                  setDowntimeData((prev) => ({ ...prev, description: e.target.value }))
                }
                placeholder="Optional details about the downtime"
                rows={3}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDowntimeOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleRecordDowntime} disabled={savingDowntime}>
              {savingDowntime ? 'Recording...' : 'Record Downtime'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
